import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Checkbox, Dialog, Input, Label, LoadingRing, Select, space } from '../../ui';
import cn from '../../ui/primitives/cn';
import { fieldHelpClasses, fieldSlotClasses } from '../../ui/primitives/input';
import { getNavOptionsForType, getNavTypes } from '../api/navApi';
import { selectSsoLinksConfig } from '../../../store/rootSelectors';
import {
  RETURN_TYPE,
  buildHref,
  denormalizeHref,
  hasRequireSso,
  isExternalLinkTarget,
  readInitialSso,
} from '../definitions';
import log from '../../../logger';
import { SAVE_BLOCKED } from '../../cmsPage/saveReadiness';
import { RequiredMark } from '../../form/components/FieldWrapper';

const byLabel = (a, b) => String(a.label || '').localeCompare(String(b.label || ''));

const Spinner = () => <LoadingRing size={25} />;

/** A field's instruction, drawn as the shared help text under its box. */
const helpClasses = cn('field-help', fieldHelpClasses, fieldSlotClasses);

/**
 * The "Link to" dialog.
 *
 * A port of `plugins/wysiwyg/cms/web/internalLinkModal.html` and its controller, deleted in
 * SURF-4809. Behaviour is deliberately identical; the notes below are the parts that look
 * arbitrary and are not.
 *
 * Shape of a selection:
 *   link type  ->  target  ->  (optionally) a chain of further targets
 *
 * A type either `hasOptions` (choose a target from a list), carries a fixed `href` (nothing
 * to choose), or neither (type a URL into the input). A target may itself `hasOptions`,
 * which opens another selector below it, as deep as the plugin wants.
 */
/*
 * Stacking note. The Angular modal set `windowClass: 'link-modal-front'` (z-index 10501) and
 * `backdropClass: 'link-modal-backdrop'` (10500), added by SURF-3568 to lift the picker above
 * the Froala toolbar. Neither is reproduced here, and passing `link-modal-front` as a
 * className did nothing: `DialogContent` also sets `tw-z-dialog` (1050), `important: true`
 * makes it `!important`, and `tailwind.css` is imported after `main.css`, so 1050 wins. The
 * class was removed rather than left as a lie. Radix's own layering should keep the picker
 * above the editor chrome, but this is the first thing to check in a browser on the Froala
 * path; `.link-modal-front` / `.link-modal-backdrop` in main.css are now dead rules.
 */
const LinkPickerDialog = ({ visible, args, onClose }) => {
  const { currentHref, returnType = RETURN_TYPE.normalizedLink, allowEmbeddedMenus } = args || {};

  const [linkTypes, setLinkTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [selectedLinkType, setSelectedLinkType] = useState();

  const [linkTargets, setLinkTargets] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState();

  // One entry per additional selector below the first, in order.
  const [additionalTargets, setAdditionalTargets] = useState([]);
  const [additionalTargetValue, setAdditionalTargetValue] = useState(null);

  const [typedHref, setTypedHref] = useState('');
  // Values live on the type object in the Angular version; kept separate here so the fetched
  // nav types stay immutable and a re-select genuinely clears them.
  const [propertyValues, setPropertyValues] = useState({});

  /*
   * SSO settings come from the browser config in redux, which `configApi.js:206` populates
   * with exactly `{enabled, siteName}` for the `sso-url` plugin and nothing else.
   *
   * The first cut of this file fetched `/plugins/plugins.json` on every open and read
   * `plugin.ssoLinks` wholesale, which was a second source of truth for the same two values
   * and leaned entirely on the plugin's `senstiveProps` list staying correct to keep
   * `secret` and `domainWhiteList` out of the browser. The redux path is projected
   * server-side to the two safe fields, is already cached, and is what
   * `ExternalLinkEditorPage` uses. Same decision, one source.
   */
  const ssoConfig = useSelector(selectSsoLinksConfig);
  const ssoEnabled = ssoConfig.enabled;
  const ssoSiteName = ssoConfig.siteName || 'the site';

  /*
   * The edit-time SSO flag, read once from the link being edited.
   *
   * Kept because it mirrors the Angular controller, but be aware it is **vestigial in both
   * versions**: `sso` is only ever emitted when `showSsoCheckbox` is true, which requires a
   * target to have been picked, and picking a target re-derives `sso` from that target's own
   * `requireSSO` param. So the preserved flag is always overwritten before it can be shown or
   * saved. Editing an existing `data-sso="true"` link and re-picking its target therefore
   * clears the flag. That is a real defect, faithfully carried over, and it needs its own
   * ticket rather than a silent behaviour change inside a migration.
   */
  const [sso, setSso] = useState(() => readInitialSso(currentHref));
  const [initialSsoPending, setInitialSsoPending] = useState(() => readInitialSso(currentHref));

  useEffect(() => {
    let live = true;
    setLoadingTypes(true);
    getNavTypes()
      .then((types) => {
        if (!live) return;
        // `embedded` types are only offered where the caller says embedded menus are legal.
        const usable = types.filter((t) => allowEmbeddedMenus || !t.embedded).sort(byLabel);
        setLinkTypes(usable);
        setSelectedLinkType(usable[0]);
        setLoadingTypes(false);
      })
      .catch((error) => {
        if (!live) return;
        log.error('Failed to load link types', error);
        setLinkTypes([]);
        setLoadingTypes(false);
      });
    return () => {
      live = false;
    };
  }, [allowEmbeddedMenus]);

  // Changing the type invalidates everything below it, including the SSO choice.
  useEffect(() => {
    if (!selectedLinkType) return;
    setSelectedTarget(undefined);
    setLinkTargets([]);
    setAdditionalTargets([]);
    setAdditionalTargetValue(null);
    // Invalidate any chained fetch still in flight under the previous type, or its response
    // would populate a selector belonging to the new one.
    targetRequestId.current += 1;
    setTypedHref('');
    setPropertyValues({});
    setSso(initialSsoPending);
    setInitialSsoPending(false);

    if (!selectedLinkType.hasOptions) return;
    let live = true;
    setLoadingTargets(true);
    getNavOptionsForType(selectedLinkType.value)
      .then((targets) => {
        if (!live) return;
        setLinkTargets([...targets].sort(byLabel));
        setLoadingTargets(false);
      })
      .catch((error) => {
        if (!live) return;
        log.error('Failed to load link targets', error);
        setLinkTargets([]);
        setLoadingTargets(false);
      });
    return () => {
      live = false;
    };
    // Deliberately keyed on `selectedLinkType` alone. `initialSsoPending` is read here but
    // must not be tracked: this effect clears it, so tracking it would re-run the effect on
    // the very change it just made and wipe the edit-time flag before the user sees it.
  }, [selectedLinkType]);

  /*
   * Sequence token for chained-target fetches.
   *
   * Every selection bumps it, and a response only lands if it is still the newest. Without
   * this, picking target A then quickly target B let A's slower response overwrite B's
   * option list: the user then picked a child of A while `targetLabel` came from B, saving a
   * link whose destination and stored label disagreed. The Angular version had the same race
   * (its responses landed on a scope that was usually destroyed first), so this is a fix
   * rather than a port.
   */
  const targetRequestId = useRef(0);

  /**
   * A target was chosen, at `index` in the chain (-1 is the first selector).
   *
   * If it has its own options, everything below it is dropped and one more selector is
   * fetched. If not, it is the answer, and the SSO box is pre-checked only when this exact
   * target was saved with `requireSSO=true`.
   */
  const onTargetChange = useCallback((target, index) => {
    setInitialSsoPending(false);
    const requestId = ++targetRequestId.current;

    // The blank option. This used to return early, which left `additionalTargetValue`
    // holding the target the user had just cleared: Save stayed enabled and wrote that old
    // href back, with `targetLabel` undefined, which blanks the row title in the navbar and
    // collection editors. Clear everything the cleared target owned instead.
    if (!target) {
      setAdditionalTargetValue(null);
      setAdditionalTargets((current) => current.slice(0, index + 1));
      setSso(false);
      return;
    }

    if (target.hasOptions) {
      setAdditionalTargetValue(null);
      setAdditionalTargets((current) => [...current.slice(0, index + 1), { loading: true }]);
      const navType = target.navType || target.value;
      getNavOptionsForType(navType, target.targetValue)
        .then((targets) => {
          if (requestId !== targetRequestId.current) return;
          setAdditionalTargets((current) => [
            ...current.slice(0, index + 1),
            { loading: false, targets: [...targets].sort(byLabel) },
          ]);
        })
        .catch((error) => {
          if (requestId !== targetRequestId.current) return;
          log.error('Failed to load link targets', error);
          setAdditionalTargets((current) => [
            ...current.slice(0, index + 1),
            { loading: false, targets: [] },
          ]);
        });
    } else {
      setAdditionalTargetValue(target.value);
    }
    setSso(isExternalLinkTarget(target.value) && hasRequireSso(target.value));
  }, []);

  const resolvedTargetValue = additionalTargetValue ?? selectedTarget?.value;
  const showSsoCheckbox = ssoEnabled && isExternalLinkTarget(resolvedTargetValue);

  const canSave = Boolean(
    selectedLinkType?.href ||
      additionalTargetValue ||
      (selectedTarget && !selectedTarget.hasOptions) ||
      typedHref
  );

  const linkTypeWithValues = useMemo(() => {
    if (!selectedLinkType?.additionalProperties) return selectedLinkType;
    return {
      ...selectedLinkType,
      additionalProperties: selectedLinkType.additionalProperties.map((p) => ({
        ...p,
        value: propertyValues[p.name] || '',
      })),
    };
  }, [selectedLinkType, propertyValues]);

  const save = useCallback(() => {
    const { href, targetLabel } = buildHref({
      linkType: linkTypeWithValues,
      selectedTarget,
      additionalTargetValue,
      typedHref,
    });
    // The SSO flag travels beside the href, never inside it, so the Froala seam can write
    // `data-sso="true"` onto the anchor and leave the href clean.
    onClose?.({
      returnVal: returnType === RETURN_TYPE.link ? denormalizeHref(href) : href,
      targetLabel,
      sso: Boolean(showSsoCheckbox && sso),
    });
  }, [
    linkTypeWithValues,
    selectedTarget,
    additionalTargetValue,
    typedHref,
    returnType,
    showSsoCheckbox,
    sso,
    onClose,
  ]);

  if (!visible) return null;

  const targetTypeLabel = selectedLinkType?.targetTypeLabel || 'Target';

  /*
   * The fields are the shared outlined ones, one column with each label above its box
   * (redesign iteration 1). The Angular layout put the labels to the left in Bootstrap's
   * horizontal form, which no longer exists: "Link Type:" wrapped in its 55px, and each box
   * started wherever its label ended. Labels above carry no colon, as every field's.
   */

  return (
    <Dialog
      title="Link to"
      onClose={() => onClose?.(null)}
      onConfirm={save}
      confirmLabel="Save"
      confirmDisabled={!canSave}
      // Grey and off, saying why, until a target is chosen or typed (SURF-4889); while the
      // choices load there is nothing to choose yet.
      confirmDisabledReason={canSave || loadingTypes || loadingTargets ? '' : SAVE_BLOCKED.required}
    >
      <div className="tw-flex tw-flex-col tw-gap-y-form-y">
        <div>
          <Label htmlFor="linkType">Link Type</Label>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Select
              id="linkType"
              value={selectedLinkType?.value || ''}
              onChange={(event) =>
                setSelectedLinkType(linkTypes.find((t) => t.value === event.target.value))
              }
            >
              {linkTypes.map((linkType) => (
                <option key={linkType.value} value={linkType.value}>
                  {linkType.label}
                </option>
              ))}
            </Select>
            {loadingTypes && <Spinner />}
          </div>
        </div>

        {selectedLinkType?.hasOptions && (
          <div>
            <Label htmlFor="linkTarget">
              {targetTypeLabel}
              <RequiredMark />
            </Label>
            {loadingTargets ? (
              <Spinner />
            ) : (
              <Select
                id="linkTarget"
                value={selectedTarget?.value || ''}
                onChange={(event) => {
                  const target = linkTargets.find((t) => t.value === event.target.value);
                  setSelectedTarget(target);
                  onTargetChange(target, -1);
                }}
              >
                <option value="" />
                {linkTargets.map((target) => (
                  <option key={target.value} value={target.value}>
                    {target.label}
                  </option>
                ))}
              </Select>
            )}
          </div>
        )}

        {selectedTarget?.hasOptions &&
          additionalTargets.map((selector, index) => {
            const previous = index === 0 ? selectedTarget : additionalTargets[index - 1]?.selected;
            return (
              <div key={`additional-${index}`}>
                <Label htmlFor={`additionalLinkTarget-${index}`}>
                  {previous?.targetTypeLabel || targetTypeLabel}
                  <RequiredMark />
                </Label>
                {selector.loading ? (
                  <Spinner />
                ) : (
                  <Select
                    id={`additionalLinkTarget-${index}`}
                    value={selector.selected?.value || ''}
                    onChange={(event) => {
                      const target = (selector.targets || []).find(
                        (t) => t.value === event.target.value
                      );
                      setAdditionalTargets((current) =>
                        current.map((entry, i) =>
                          i === index ? { ...entry, selected: target } : entry
                        )
                      );
                      onTargetChange(target, index);
                    }}
                  >
                    {/*
                      No blank option here, matching the Angular template: only the
                      first-level select offered one (`internalLinkModal.html:41` had it,
                      the chained select at :65 did not).
                    */}
                    {(selector.targets || []).map((target) => (
                      <option key={target.value} value={target.value}>
                        {target.label}
                      </option>
                    ))}
                  </Select>
                )}
              </div>
            );
          })}

        {selectedLinkType && !selectedLinkType.hasOptions && !selectedLinkType.href && (
          <div>
            <Label htmlFor="linkTargetHref">
              {targetTypeLabel}
              <RequiredMark />
            </Label>
            <Input
              id="linkTargetHref"
              value={typedHref}
              aria-describedby={selectedLinkType.instruction ? 'linkTargetHref-help' : undefined}
              onChange={(event) => setTypedHref(event.target.value)}
            />
            {selectedLinkType.instruction && (
              <div id="linkTargetHref-help" className={helpClasses}>
                {selectedLinkType.instruction}
              </div>
            )}
          </div>
        )}

        {(selectedLinkType?.additionalProperties || []).map((property) => (
          <div key={property.name}>
            <Label htmlFor={`prop_${property.name}`}>{property.label}</Label>
            <Input
              id={`prop_${property.name}`}
              name={`prop_${property.name}`}
              aria-describedby={property.instruction ? `prop_${property.name}-help` : undefined}
              value={propertyValues[property.name] || ''}
              onChange={(event) =>
                setPropertyValues((current) => ({
                  ...current,
                  [property.name]: event.target.value,
                }))
              }
            />
            {property.instruction && (
              <div id={`prop_${property.name}-help`} className={helpClasses}>
                {property.instruction}
              </div>
            )}
          </div>
        ))}

        {showSsoCheckbox && (
          <div>
            <label className="tw-inline-flex tw-items-center">
              <Checkbox id="ssoLink" checked={sso} onChange={(next) => setSso(!!next)} />
              <span style={{ marginLeft: space[8] }}>SSO to {ssoSiteName}</span>
            </label>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default LinkPickerDialog;
